import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDownIcon } from '@rsos/assets/icons';
import {
  StyledScrollShadow,
  ScrollShadowIconWrapper,
} from './ScrollShadow.styles';

/**
 * A component that shows a shadow at the bottom of a scrollable container
 * to indicate there is more content below. The shadow disappears when
 * the user scrolls to the bottom.
 *
 * @param {Object} props - Component props
 * @param {React.RefObject} props.containerRef - Reference to the scrollable container
 * @param {boolean} [props.forceShow] - Force the shadow to be visible regardless of scroll position
 * @returns {JSX.Element} The ScrollShadow component
 */
const ScrollShadow = ({ containerRef, forceShow = false }) => {
  const [showShadow, setShowShadow] = useState(false);

  // Handle scroll to detect if user is at bottom
  const handleScroll = useCallback(() => {
    const container = containerRef?.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
    setShowShadow(!isAtBottom && scrollHeight > clientHeight);
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    handleScroll();

    container.addEventListener('scroll', handleScroll);

    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [handleScroll, containerRef]);

  const shouldShow = forceShow || showShadow;
  const shadow = shouldShow ? (
    <StyledScrollShadow>
      <ScrollShadowIconWrapper>
        <ChevronDownIcon width={18} height={18} />
      </ScrollShadowIconWrapper>
    </StyledScrollShadow>
  ) : null;

  return shadow;
};

export default ScrollShadow;
